/* eslint-disable @typescript-eslint/naming-convention */
import { Model } from 'app/model/backendmodel';

import {
    irTypesConst, irStatusConst, IIRCapabilities, irVersionStatusConst,
    irStartingStuckMs, irStoppingStuckMs, irDefaultInstanceName, irVNetModeTypes
} from 'app/integrationruntime/integrationruntime.constants';
import * as ssisConstants from 'app/integrationruntime/ir-authoring/ir-ssis/ssis.constants';
import { IntegrationRuntime } from 'app/model/integrationRuntime/integrationRuntime';
import { ManagedIntegrationRuntime } from 'app/model/integrationRuntime/managed.integrationRuntime';
import moment from 'moment';
import 'moment-duration-format';
import { IActivityRunOutput, IActivityRunExecutionDetails } from 'app/model/monitoring/monitoring-models';
import { format, getStringOrDefaultValue, stringsAreEqualCaseInsensitive, stringIsNullOrEmpty, convertObjectToString } from 'app/shared/utils/string.util';
import { IVirtualMachineSize } from 'app/interfaces/IAzureResource';
import { formatRegionString, localizeRegionString, regions } from 'app/shared/utils/region.util';
import { timeFormatWithSeconds } from 'app/shared/shared-constants';
import { LoggerService } from 'app/logger/logger.service';
import { GlobalVariables } from 'app/services/globals';
import ShellAPI from 'a365-shell-api';
import { GetIRTypeFromLocalModel, GetIRSubTypeFromLocalModel, IGetStatusResult } from 'shell-models-lib';
import { LocalizationService } from 'app/services/localization.service';
import { formatTimeWithRegionAndTimezone } from 'app/shared/utils/time-format.util';

function isControlActivityType(activityType: string): boolean {
    return activityType === 'Switch'
        || activityType === 'IfCondition'
        || activityType === 'ForEach'
        || activityType === 'Until'
        || activityType === 'Filter'
        || activityType === 'ExecutePipeline'
        || activityType === 'SetVariable'
        || activityType === 'AppendVariable'
        || activityType === 'Validation'
        || activityType === 'Wait'
        || activityType === 'WebHook'
        || activityType === 'Fail';
}

export class IntegrationRuntimeAdapters {
    public static GetIRTypeFromLocalModel = GetIRTypeFromLocalModel;
    public static GetIRSubTypeFromLocalModel = GetIRSubTypeFromLocalModel;
    protected static readonly className = 'IntegrationRuntimeAdapters';
    //#region Localization functions
    public static LocalizeString(value?: string): string {
        if (value == null) {
            return '---';
        }
        return value;
    }

    public static LocalizeAvailableMemory(value?: Model.IntegrationRuntimeNodeMonitoringData, irStatus?: string): string {
        if (value == null || value.availableMemoryInMB == null) {
            return '---';
        }
        if (!this.IsIRConnected(irStatus)) {
            return '---';
        }
        return `${value.availableMemoryInMB}MB`;
    }

    public static LocalizeCpuUtilization(value?: Model.IntegrationRuntimeNodeMonitoringData, irStatus?: string): string {
        if (value == null || value.cpuUtilization == null) {
            return '---';
        }
        if (!this.IsIRConnected(irStatus)) {
            return '---';
        }
        return `${value.cpuUtilization}%`;
    }

    public static LocalizeConcurrentJobs(
        properties: Model.IntegrationRuntimeResource,
        value: Model.IntegrationRuntimeNodeMonitoringData,
        irStatus?: string,
        irNode?: Model.SelfHostedIntegrationRuntimeNode
    ): string {
        const irType = IntegrationRuntimeAdapters.GetIRTypeFromServiceResponse(properties);
        if (irType === irTypesConst.AzureSSIS) {
            let maxCount: string = '---';
            const azureSSISIRProperties = properties.properties as Model.ManagedIntegrationRuntime;
            const computeProperties = azureSSISIRProperties.typeProperties.computeProperties;
            if (computeProperties != null && computeProperties.maxParallelExecutionsPerNode != null) {
                maxCount = computeProperties.maxParallelExecutionsPerNode.toString();
            }

            let currentCount: string = '---';
            if (value != null && value.concurrentJobsRunning != null) {
                currentCount = value.concurrentJobsRunning.toString();
            }
            return `${currentCount}/${maxCount}`;
        }
        if (value == null || value.concurrentJobsRunning == null || value.concurrentJobsLimit == null || irNode == null) {
            return '---/---/---';
        }
        if (!this.IsIRConnected(irStatus)) {
            return '---/---/---';
        }
        const { runningValue, availableValue, maxValue } = IntegrationRuntimeAdapters.calculateLimitConcurrentJob(value, irNode);
        return `${runningValue}/${availableValue}/${maxValue}`;
    }

    public static calculateLimitConcurrentJob(
        monitorData: Model.IntegrationRuntimeNodeMonitoringData,
        irNode: Model.SelfHostedIntegrationRuntimeNode
    ): { runningValue: number, availableValue: number, maxValue: number } {
        // limitValue from monitorData => availableValue
        const availableValue = monitorData.concurrentJobsLimit;
        // limitValue from getStatus => limitValue
        const limitedValue = irNode.concurrentJobsLimit != null
            ? irNode.concurrentJobsLimit
            : irNode.maxConcurrentJobs != null
                ? Math.round(irNode.maxConcurrentJobs / 4)
                : availableValue;

        return {
            runningValue: monitorData.concurrentJobsRunning,
            availableValue,
            maxValue: limitedValue
        };
    }

    public static LocalizeNetwork(value?: Model.IntegrationRuntimeNodeMonitoringData, irStatus?: string): string {
        if (value == null || value.sentBytes == null || value.receivedBytes == null) {
            return '---/---';
        }
        if (!this.IsIRConnected(irStatus)) {
            return '---/---';
        }
        return `${value.receivedBytes.toFixed(2)}KBps/${value.sentBytes.toFixed(2)}KBps`;
    }

    public static LocalizeIRTypeString(irType: string, containSubType: boolean = false): string {
        switch (irType) {
            case irTypesConst.SelfHosted:
            case irTypesConst.Emulator:
                return stringResources.SelfHostedLabel;
            case irTypesConst.Azure:
                return containSubType ? stringResources.IRType_AzurePublic : stringResources.AzureLabel;
            case irTypesConst.ManagedVNet:
                return containSubType ? stringResources.IRType_ManagedVNet : stringResources.AzureLabel;
            case irTypesConst.AzureSSIS:
                return stringResources.IRType_AzureSSIS;
            case irTypesConst.AzureSSISLinked:
                return stringResources.IRType_AzureSSIS;
            case irTypesConst.AzureSSISShared:
                return stringResources.IRType_AzureSSIS;
            case irTypesConst.Linked:
                return containSubType ? stringResources.IRType_Linked : stringResources.SelfHostedLabel;
            case irTypesConst.Airflow:
                return stringResources.AzureLabel;
            default:
                return stringResources.NA;
        }
    }

    public static LocalizeIRSubTypeString(irType: string, linkCount: number = 0): string {
        switch (irType) {
            case irTypesConst.SelfHosted:
                return linkCount > 0 ? stringResources.Shared : '---';
            case irTypesConst.Emulator:
                return stringResources.Emulator;
            case irTypesConst.Azure:
                return stringResources.Public;
            case irTypesConst.ManagedVNet:
                return stringResources.IRType_ManagedVNet;
            case irTypesConst.AzureSSIS:
                return '---';
            case irTypesConst.AzureSSISShared:
                return stringResources.Shared;
            case irTypesConst.AzureSSISLinked:
                return stringResources.Linked;
            case irTypesConst.Linked:
                return stringResources.Linked;
            case irTypesConst.Airflow:
                return stringResources.Airflow_NonPreviewLabel;
            default:
                return stringResources.NA;
        }
    }

    public static LocalizeIRStatusString(status?: string, errorCount?: number, lastOperationType?: string): string {
        switch (status) {
            case irStatusConst.NeedRegistration:
                return stringResources.IRStatus_Unavailable;
            case irStatusConst.Online:
                return stringResources.Running;
            case irStatusConst.Limited:
                return stringResources.IRStatus_Limited;
            case irStatusConst.Inactive:
                return stringResources.IRStatus_Inactive;
            case irStatusConst.Offline:
                return stringResources.IRStatus_Unavailable;
            case irStatusConst.Upgrading:
                return stringResources.Upgrading;
            case irStatusConst.Initializing:
                return stringResources.Initializing;
            case irStatusConst.InitializeFailed:
                return stringResources.IRStatus_InitializeFailed;
            case irStatusConst.Initial:
            case irStatusConst.Stopped:
                if (errorCount != null && errorCount > 0) {
                    if (lastOperationType && lastOperationType.toLowerCase() === 'start') {
                        return stringResources.IRStatus_FailedToStart;
                    }
                    return stringResources.IRStatus_StoppedWithError;
                }
                return stringResources.Status_Stopped;
            case irStatusConst.Stopping:
                return stringResources.Stopping;
            case irStatusConst.Started:
                return stringResources.Running;
            case irStatusConst.Starting:
                return stringResources.Status_Starting;
            case irStatusConst.Available:
                return stringResources.Running;
            case irStatusConst.Recycling:
                return stringResources.IRStatus_Recycling;
            case irStatusConst.Unavailable:
                return stringResources.IRStatus_Unavailable;
            case irStatusConst.Unauthorized:
                return stringResources.IRStatus_Unauthorized;
            case irStatusConst.Succeeded:
                return stringResources.Succeeded;
            case irStatusConst.Failed:
                return stringResources.Failed;
            case irStatusConst.Provisioning:
                return stringResources.IRStatus_Provisioning;
            case irStatusConst.ProvisionFailed:
                return stringResources.IRStatus_ProvisionFailed;
            case irStatusConst.ProvisionSucceeded:
                return stringResources.IRStatus_ProvisionSucceeded;
            case irStatusConst.NotExist:
                return stringResources.IRError_NotExist;
            case irStatusConst.NotFound:
                return stringResources.IRError_NotFound;
            default:
                return stringResources.NA;
        }
    }

    public static LocalizeIRInteractiveStatusString(status?: ShellAPI.integrationruntimeVnetApi.irInteractiveStatusType): string {
        switch (status) {
            case 'Disabled':
                return stringResources.OffLabel;
            case 'Enabled':
                return stringResources.OnLabel;
            case 'Starting':
                return stringResources.Status_Starting;
            case 'Stopping':
                return stringResources.Stopping;
            case 'Loading':
                return stringResources.Refreshing;
            default:
                return '';
        }
    }

    public static LocalizeSelfhostedIRStatusTooltip(status: string, originStatus?: string): string {
        return this.LocalizeIRStatusTooltip(status, undefined, originStatus);
    }

    public static LocalizeSSISIRStatusTooltip(status: string, errorCount: number): string {
        return this.LocalizeIRStatusTooltip(status, errorCount, undefined);
    }

    public static LocalizeAzureIRStatusTooltip(status?: string): string {
        return this.LocalizeIRStatusTooltip(status, undefined, undefined);
    }

    public static LocalizeRequestedNodeCount(computeProperties?: Model.IntegrationRuntimeComputeProperties): string {
        if (computeProperties == null) {
            return '---';
        }
        if (computeProperties.numberOfNodes == null) {
            return '0';
        }
        return computeProperties.numberOfNodes.toString();
    }

    public static LocalizeNodeRole(isActiveDispatcher?: boolean): string {
        switch (isActiveDispatcher) {
            case true:
                return stringResources.IRMonitor_Node_Role_Dispatcher;
            case false:
                return stringResources.WorkerLabel;
            default:
                return stringResources.NA;
        }
    }

    public static LocalizeNodeCredentialSync(capabilities?: { [propertyName: string]: string }): string {
        if (capabilities === undefined) {
            return stringResources.NA;
        }
        switch (capabilities['credentialInSync']) {
            case 'True':
                return stringResources.IRMonitor_Node_Credential_InSync;
            case 'False':
                return stringResources.IRMonitor_Node_Credential_NotInSync;
            default:
                return stringResources.NA;
        }
    }

    public static isValidDefaultIR(ir: IntegrationRuntime, vnetOnly: boolean = false): boolean {
        if (ir == null) {
            return false;
        }
        if (ir.name === irDefaultInstanceName && ir instanceof ManagedIntegrationRuntime) {
            const irType = IntegrationRuntimeAdapters.GetIRTypeFromLocalModel(ir);
            if (vnetOnly && irType !== irTypesConst.ManagedVNet) {
                return false;
            }
            if (irType !== irTypesConst.Azure && irType !== irTypesConst.ManagedVNet) {
                return false;
            }
            return formatRegionString(ir.computeLocation) === formatRegionString(regions.AutoResolve);
        }
        return false;
    }

    public static LocalizeDateTimeString(dateTime: Date, logger: LoggerService, timezone?: string, format?: string): string {
        const localizationService = new LocalizationService();
        if (dateTime == null) {
            return '---';
        }
        return formatTimeWithRegionAndTimezone(dateTime, localizationService.getLanguageRegionCode(), logger, timezone);
    }

    public static LocalizeMomentString(time?: moment.Moment, timezone?: string): string {
        if (time == null) {
            return '---';
        }
        if (stringIsNullOrEmpty(timezone)) {
            return moment.utc(time).local().format(timeFormatWithSeconds);
        }
        if (timezone === 'UTC' || timezone === 'utc') {
            return moment.utc(time).format(timeFormatWithSeconds);
        }
        return moment.tz(time, timezone).format(timeFormatWithSeconds);
    }

    public static LocalizeSSISStatusErrorMessage(errorCode: string | undefined, parameters: string[], defaultMessage?: string): string {
        switch (errorCode) {
            case 'AzureSqlConnectionFailure':
                return format(stringResources.IRError_SSIS_AzureSqlConnectionFailure, ...parameters, defaultMessage || '');
            case 'CatalogDbAlreadyExist':
                return format(stringResources.IRError_SSIS_CatalogDbAlreadyExist, ...parameters, defaultMessage || '');
            case 'InputCannotBeNullOrEmpty':
                return format(stringResources.IRError_SSIS_InputCannotBeNullOrEmpty, ...parameters, defaultMessage || '');
            case 'IntegrationRuntimeCannotModify':
                return format(stringResources.IRError_SSIS_IntegrationRuntimeCannotModify, ...parameters, defaultMessage || '');
            case 'IntegrationRuntimeCannotStart':
                return format(stringResources.IRError_SSIS_IntegrationRuntimeCannotStart, ...parameters, defaultMessage || '');
            case 'IntegrationRuntimeCannotStop':
                return format(stringResources.IRError_SSIS_IntegrationRuntimeCannotStop, ...parameters, defaultMessage || '');
            case 'InvalidCatalogDbVersion':
                return format(stringResources.IRError_SSIS_InvalidCatalogDbVersion, ...parameters, defaultMessage || '');
            case 'InvalidIntegrationRuntimeNodeNumber':
                return format(stringResources.IRError_SSIS_InvalidIntegrationRuntimeNodeNumber, ...parameters, defaultMessage || '');
            case 'InvalidVnetConfiguration':
                return format(stringResources.IRError_SSIS_InvalidVnetConfiguration, ...parameters, defaultMessage || '');
            case 'InvalidPublicIPSpecified':
                return format(stringResources.IRError_SSIS_InvalidPublicIPSpecified, ...parameters, defaultMessage || '');
            case 'InvalidEdition':
                return format(stringResources.IRError_SSIS_InvalidEdition, ...parameters, defaultMessage || '');
            case 'InvalidLicenseType':
                return format(stringResources.IRError_SSIS_InvalidLicenseType, ...parameters, defaultMessage || '');
            case 'PropertyValueNotEnabled':
                return format(stringResources.IRError_SSIS_PropertyValueNotEnabled, ...parameters, defaultMessage || '');
            case 'IRAlreadyExists':
                return format(stringResources.IRError_SSIS_IRAlreadyExists, ...parameters, defaultMessage || '');
            case 'IRNotSupportEncryption':
                return format(stringResources.IRError_SSIS_IRNotSupportEncryption, ...parameters, defaultMessage || '');
            case 'ManagedIRAuthKeyOperationNotSupported':
                return format(stringResources.IRError_SSIS_ManagedIRAuthKeyOperationNotSupported, ...parameters, defaultMessage || '');
            case 'OperationNotAllowedInCurrentIRState':
                return format(stringResources.IRError_SSIS_OperationNotAllowedInCurrentIRState, ...parameters, defaultMessage || '');
            case 'UnsupportedIRType':
                return format(stringResources.IRError_SSIS_UnsupportedIRType, ...parameters, defaultMessage || '');
            case 'CatalogPricingTierCannotBeNullOrEmptyForNonManagedInstance':
                return format(stringResources.IRError_SSIS_CatalogPricingTierCannotBeNullOrEmptyForNonManagedInstance, ...parameters, defaultMessage || '');
            case 'CatalogPricingTierCannotBeSpecifiedForManagedInstance':
                return format(stringResources.IRError_SSIS_CatalogPricingTierCannotBeSpecifiedForManagedInstance, ...parameters, defaultMessage || '');
            case 'NonAzureSqlDbServerIsNotAllowed':
                return format(stringResources.IRError_SSIS_NonAzureSqlDbServerIsNotAllowed, ...parameters, defaultMessage || '');
            case 'AzureSqlRegionError':
                return format(stringResources.IRError_SSIS_AzureSqlRegionError, ...parameters, defaultMessage || '');
            case 'InvalidCatalogPricingTier':
                return format(stringResources.IRError_SSIS_InvalidCatalogPricingTier, ...parameters, defaultMessage || '');
            case 'CatalogEndpointCannotModifyForNonInitialIR':
                return format(stringResources.IRError_SSIS_CatalogEndpointCannotModifyForNonInitialIR, ...parameters, defaultMessage || '');
            case 'CatalogDbCreationFailure':
                return format(stringResources.IRError_SSIS_CatalogDbCreationFailure, ...parameters, defaultMessage || '');
            case 'CatalogDbSchemaInstallationFailure':
                return format(stringResources.IRError_SSIS_CatalogDbSchemaInstallationFailure, ...parameters, defaultMessage || '');
            case 'CatalogDbUserCreationFailure':
                return format(stringResources.IRError_SSIS_CatalogDbUserCreationFailure, ...parameters, defaultMessage || '');
            case 'CoreQuotaReached':
                return format(stringResources.IRError_SSIS_CoreQuotaReached, ...parameters, defaultMessage || '');
            case 'CustomSetupScriptFailure':
                return format(stringResources.IRError_SSIS_CustomSetupScriptFailure, ...parameters, defaultMessage || '');
            case 'ContainedDatabaseAuthenticationIsRequired':
                return format(stringResources.IRError_SSIS_ContainedDatabaseAuthenticationIsRequired, ...parameters, defaultMessage || '');
            case 'InvalidCatalogDb':
                return format(stringResources.IRError_SSIS_InvalidCatalogDb, ...parameters, defaultMessage || '');
            case 'SqlInvalidElasticName':
                return format(stringResources.IRError_SSIS_SqlInvalidElasticName, ...parameters, defaultMessage || '');
            case 'AdfMsiNotFound':
                return format(stringResources.IRError_SSIS_AdfMsiNotFound, ...parameters, defaultMessage || '');
            case 'InvalidMaxParallelCount':
                return format(stringResources.IRError_SSIS_InvalidMaxParallelCount, ...parameters, defaultMessage || '');
            case 'CustomSetupScriptBlobContainerInaccessible':
                return format(stringResources.IRError_SSIS_CustomSetupScriptBlobContainerInaccessible, ...parameters, defaultMessage || '');
            case 'CustomSetupScriptNotFound':
                return format(stringResources.IRError_SSIS_CustomSetupScriptNotFound, ...parameters, defaultMessage || '');
            case 'IntegrationRuntimeStartingLongerThanUsual':
                return format(stringResources.IRError_SSIS_IntegrationRuntimeStartingLongerThanUsual, ...parameters, defaultMessage || '');
            case 'IntegrationRuntimeStoppingLongerThanUsual':
                return format(stringResources.IRError_SSIS_IntegrationRuntimeStoppingLongerThanUsual, ...parameters, defaultMessage || '');
            case 'VNetResourceGroupLockedOrReferenceProvisionMessage':
                return format(stringResources.IRError_SSIS_VNetResourceGroupLockedOrReferenceProvisionMessage, ...parameters, defaultMessage || '');
            case 'VNetResourceGroupLockedOrReferenceDeprovisionMessage':
                return format(stringResources.IRError_SSIS_VNetResourceGroupLockedOrReferenceDeprovisionMessage, ...parameters, defaultMessage || '');
            case 'ExpressCustomSetupFailure':
                return format(stringResources.IRError_SSIS_ExpressCustomSetupFailure_Popup, ...parameters);
            case 'JoinVNetPermissionNotGranted':
                return format(stringResources.IRError_SSIS_JoinVNetPermissionNotGranted, ...parameters, defaultMessage || '');
            case 'ExpressVNetInjectionNotEnabled':
                return format(stringResources.IRError_SSIS_ExpressVNetInjectionNotEnabled, ...parameters, defaultMessage || '');
            case 'ExpressVNetInjectionNotSupported':
                return format(stringResources.IRError_SSIS_ExpressVNetInjectionNotSupported, ...parameters, defaultMessage || '');
            case 'InvalidPayloadFormat':
                return format(stringResources.IRError_SSIS_InvalidPayloadFormat, ...parameters, defaultMessage || '');
            case 'ExpressVNetInjectionAlreadyUsed':
                return format(stringResources.IRError_SSIS_ExpressVNetInjectionAlreadyUsed, ...parameters, defaultMessage || '');
            default:
                return defaultMessage || '';
        }
    }

    public static LocalizeDiagnoseConnectivityErrorMessage(errorCode: string | undefined, defaultMessage?: string): string {
        switch (errorCode) {
            case 'InvalidInput':
                return format(stringResources.IRError_SSIS_DiagnoseConnect_InvalideInput, defaultMessage || '');
            case 'MisconfiguredDnsSettings':
                return format(stringResources.IRError_SSIS_DiagnoseConnect_MisconfiguredDnsSettings, defaultMessage || '');
            case 'MisconfiguredNsgSettings':
                return format(stringResources.IRError_SSIS_DiagnoseConnect_MisconfiguredNsgSettings, defaultMessage || '');
            case 'ServerNotAllowRemoteConenction':
                return format(stringResources.IRError_SSIS_DiagnoseConnect_ServerNotAllowRemoteConenction, defaultMessage || '');
            case 'FirewallOrNetworkIssue':
                return format(stringResources.IRError_SSIS_DiagnoseConnect_FirewallOrNetworkIssue, defaultMessage || '');
            case 'NetworkInstable':
                return format(stringResources.IRError_SSIS_DiagnoseConnect_NetworkInstable, defaultMessage || '');
            case 'PSPingExecutionTimeout':
                return format(stringResources.IRError_SSIS_DiagnoseConnect_PSPingExecutionTimeout, defaultMessage || '');
            case 'GenericIssues':
                return format(stringResources.IRError_SSIS_DiagnoseConnect_GenericIssues, defaultMessage || '');
            case 'Succeeded':
                return format(stringResources.IRError_SSIS_DiagnoseConnect_Succeeded, defaultMessage || '');
            default:
                return defaultMessage || '';
        }
    }

    public static LocalizeErrorList(errors?: Model.ManagedIntegrationRuntimeError[], lastOperationResult?: Model.ManagedIntegrationRuntimeOperationResult): string {
        let result: string = '';
        let errorCount: number = 0;
        if (lastOperationResult != null && stringsAreEqualCaseInsensitive(lastOperationResult.result, 'Failed')) {
            errorCount += 1;
            const lastOperationMessage = format(stringResources.IRError_SSIS_LastOperation,
                lastOperationResult.type == null ? '' : lastOperationResult.type,
                lastOperationResult.result == null ? '' : lastOperationResult.result);
            result = result + `${stringResources.Error} ${errorCount}:\n` +
                `  ${lastOperationMessage}\n` +
                `  ${stringResources.ErrorCode}: ${lastOperationResult.errorCode}\n` +
                `  ${stringResources.ErrorMessage}: ${IntegrationRuntimeAdapters.LocalizeSSISStatusErrorMessage(lastOperationResult.errorCode, lastOperationResult.parameters || [])}\n` +
                `  ${stringResources.ActivityId}: ${lastOperationResult.activityId}\n\n`;
        }
        if (errors != null && errors.length > 0) {
            for (const error of errors) {
                errorCount += 1;
                result = result + `${stringResources.Error} ${errorCount}:\n` +
                    `  ${stringResources.ErrorCode}: ${error.code}\n` +
                    `  ${stringResources.ErrorMessage}: ${IntegrationRuntimeAdapters.LocalizeSSISStatusErrorMessage(error.code, error.parameters || [], error.message)}\n\n`;
            }
        }
        return result;
    }

    public static LocalizeSelfhostedNodeErrorMessage(nodeStatus: Model.SelfHostedIntegrationRuntimeNode): { message: string, syncAction: boolean } {
        let errorMessage = '';
        if (nodeStatus.status === irStatusConst.Upgrading) {
            errorMessage += `${stringResources.IRError_Selfhosted_Upgrading}\n`;
        }
        if (nodeStatus.status === irStatusConst.Offline) {
            errorMessage += `${stringResources.IRError_Selfhosted_Offline}\n`;
        }
        if (!this.IsIRConnected(nodeStatus.status)) {
            return { message: errorMessage, syncAction: false };
        }
        let syncAction: boolean = false;
        if (nodeStatus.capabilities != null) {
            const capabilities = nodeStatus.capabilities as IIRCapabilities;
            if (!this.getCapabilityBooleanValue(true, capabilities.nodeEnabled)) {
                errorMessage += `${stringResources.IRError_Selfhosted_NotSameVersion}\n`;
            } else if (!this.getCapabilityBooleanValue(true, capabilities.connectedToResourceManager)) {
                errorMessage += `${stringResources.IRError_Selfhosted_ConnecteToResourceManager}\n`;
            }
            if (!this.getCapabilityBooleanValue(true, capabilities.credentialInSync)) {
                if (nodeStatus.isActiveDispatcher) {
                    errorMessage += `${stringResources.IRError_Selfhosted_CredentialNotSyncDispatcher}\n`;
                    syncAction = true;
                } else {
                    errorMessage += `${stringResources.IRError_Selfhosted_CredentialNotSyncWorker}\n`;
                }
            }
            if (!this.getCapabilityBooleanValue(true, capabilities.serviceBusConnected)) {
                errorMessage += `${stringResources.IRError_Selfhosted_ServiceBusConnection}\n`;
            }
            if (!this.getCapabilityBooleanValue(true, capabilities.httpsPortEnabled)) {
                errorMessage += `${stringResources.IRError_Selfhosted_HttpPort}\n`;
            }
            if (this.getCapabilityBooleanValue(false, capabilities.noEnoughDiskSpace)) {
                errorMessage += `${stringResources.IRError_Selfhosted_NoEnoughDiskSpace}\n`;
            }
        }
        return {
            message: errorMessage,
            syncAction
        };
    }
    //#endregion

    //#region Get IR Properties from some data contract
    public static GetIRRegionFromServiceResponse(properties: Model.ManagedIntegrationRuntime): string {
        if (stringsAreEqualCaseInsensitive(properties.type, irTypesConst.SSIS)) {
            return formatRegionString(properties.typeProperties.linkedInfo.sharedIntegrationRuntimeLocation);
        } else if (properties.typeProperties.computeProperties?.location != null) {
            return formatRegionString(properties.typeProperties.computeProperties.location);
        }
        return '---';
    }

    public static GetIRTypeFromServiceResponse(irItem: Model.IntegrationRuntimeResource): string {
        if (stringsAreEqualCaseInsensitive(irItem.properties.type, irTypesConst.Managed) || stringsAreEqualCaseInsensitive(irItem.properties.type, irTypesConst.SSIS)) {
            const resourceProperties = irItem.properties as Model.ManagedIntegrationRuntime;
            if (resourceProperties.typeProperties.ssisProperties != null || resourceProperties.typeProperties.linkedInfo != null) {
                return irTypesConst.AzureSSIS;
            }
            if (resourceProperties.managedVirtualNetwork) {
                return irTypesConst.ManagedVNet;
            }
            return irTypesConst.Azure;
        } else if (stringsAreEqualCaseInsensitive(irItem.properties.type, irTypesConst.SelfHosted)) {
            const resourceProperties = irItem.properties as Model.SelfHostedIntegrationRuntime;
            if (resourceProperties.typeProperties != null) {
                if (resourceProperties.typeProperties.linkedInfo != null) {
                    const authType = resourceProperties.typeProperties.linkedInfo.authorizationType;
                    if (stringsAreEqualCaseInsensitive(authType, 'Rbac')) {
                        return irTypesConst.Linked;
                    }
                }
                if (resourceProperties.typeProperties.subType === irTypesConst.Emulator) {
                    return irTypesConst.Emulator;
                }
            }

            return irTypesConst.SelfHosted;
        }
        return irItem.properties.type;
    }

    public static GetIRSubTypeFromServiceResponse(irItem: Model.IntegrationRuntimeResource): string {
        if (stringsAreEqualCaseInsensitive(irItem.properties.type, irTypesConst.Managed) || stringsAreEqualCaseInsensitive(irItem.properties.type, irTypesConst.SSIS)) {
            const resourceProperties = irItem.properties as Model.ManagedIntegrationRuntime;
            if (resourceProperties.typeProperties != null && resourceProperties.typeProperties.sharingProperties != null) {
                return irTypesConst.AzureSSISShared;
            }
            if (resourceProperties.typeProperties != null && resourceProperties.typeProperties.linkedInfo != null) {
                return irTypesConst.AzureSSISLinked;
            }
        }
        return undefined;
    }

    public static GetInteractiveQueryStatus(status: Model.IntegrationRuntimeStatus): ShellAPI.integrationruntimeVnetApi.irInteractiveStatusType {
        const managedStatusResponse = status as Model.ManagedIntegrationRuntimeStatus;
        if (managedStatusResponse == null) {
            return 'Disabled';
        }
        if (managedStatusResponse.state === irStatusConst.Initializing) {
            return 'Invalid';
        }
        if (managedStatusResponse.typeProperties == null) {
            return 'Disabled';
        }

        if (managedStatusResponse.typeProperties.dataMovementState !== irStatusConst.Succeeded) {
            return 'Invalid';
        }

        const interactiveQueryStatus = managedStatusResponse.typeProperties.interactiveQuery;
        if (interactiveQueryStatus == null) {
            return 'Disabled';
        }
        if (stringsAreEqualCaseInsensitive(interactiveQueryStatus.status, 'Enabled')) {
            return 'Enabled';
        }
        if (stringsAreEqualCaseInsensitive(interactiveQueryStatus.status, 'Enabling')) {
            return 'Starting';
        }
        return 'Disabled';
    }

    public static GetIRNodeSize(properties: Model.ManagedIntegrationRuntime, sizeList: IVirtualMachineSize[]): string {
        let nodeSize = '';
        if (properties.typeProperties.computeProperties != null && properties.typeProperties.computeProperties.nodeSize != null) {
            nodeSize = properties.typeProperties.computeProperties.nodeSize;
        }
        const sizeDetail = sizeList.find((item) => stringsAreEqualCaseInsensitive(item.name, nodeSize));
        if (sizeDetail) {
            nodeSize = `${sizeDetail.name.replace('Standard_', '')}, ${sizeDetail.numberOfCores} ${stringResources.Core}, ${sizeDetail.memoryInMB}MB`;
        }
        if (properties.typeProperties.ssisProperties != null && properties.typeProperties.ssisProperties.edition != null) {
            nodeSize = nodeSize + `, ${properties.typeProperties.ssisProperties.edition}`;
        }
        return nodeSize;
    }

    public static GetIRServiceEndpoint(properties: Model.ManagedIntegrationRuntime): string {
        if (properties.typeProperties.ssisProperties != null) {
            if (!properties.typeProperties.ssisProperties.catalogInfo) {
                return undefined;
            } else if (properties.typeProperties.ssisProperties.catalogInfo.catalogServerEndpoint != null) {
                return properties.typeProperties.ssisProperties.catalogInfo.catalogServerEndpoint;
            }
        }
        return '---';
    }

    public static GetSSISIRRuntimeVersion(properties: Model.ManagedIntegrationRuntime): string {
        if (properties.typeProperties.ssisProperties != null) {
            return stringsAreEqualCaseInsensitive(properties.typeProperties.ssisProperties.runtimeVersion, ssisConstants.ssisRuntimeVersion.Latest) ? stringResources.IRCreation_SSIS_RuntimeVersion_Azure_SSIS :
                stringResources.IRCreation_SSIS_RuntimeVersion_SQL_2017;
        }
        return undefined;
    }

    public static GetIRDualStandbyPairName(properties: Model.ManagedIntegrationRuntime): string {
        if (properties.typeProperties.ssisProperties != null) {
            if (!properties.typeProperties.ssisProperties.catalogInfo) {
                return undefined;
            } else if (properties.typeProperties.ssisProperties.catalogInfo.dualStandbyPairName) {
                return properties.typeProperties.ssisProperties.catalogInfo.dualStandbyPairName;
            }
        }
        return undefined;
    }

    public static GetVnetId(properties: Model.ManagedIntegrationRuntime): string {
        if (properties != null &&
            properties.typeProperties != null &&
            properties.typeProperties.computeProperties != null
        ) {
            return !properties.typeProperties.computeProperties.vNetProperties ? null : properties.typeProperties.computeProperties.vNetProperties.vNetId;
        }
        return null;
    }

    public static GetSubnetName(properties: Model.ManagedIntegrationRuntime): string {
        if (properties != null &&
            properties.typeProperties != null &&
            properties.typeProperties.computeProperties != null
        ) {
            return !properties.typeProperties.computeProperties.vNetProperties ? null : properties.typeProperties.computeProperties.vNetProperties.subnet;
        }
        return null;
    }

    public static GetVNetModeType(properties: Model.ManagedIntegrationRuntime): string {
        let subnetId = this.GetVNetExpressSubnetId(properties);
        if (!stringIsNullOrEmpty(subnetId)) {
            return irVNetModeTypes.Express;
        }
        subnetId = this.GetVnetId(properties);
        if (!stringIsNullOrEmpty(subnetId)) {
            return irVNetModeTypes.Standard;
        }
        subnetId = this.GetVnetStandardSubnetId(properties);
        if (!stringIsNullOrEmpty(subnetId)) {
            return irVNetModeTypes.Standard;
        }
        return null;
    }

    public static GetVnetStandardSubnetId(properties: Model.ManagedIntegrationRuntime): string {
        const subnetId = properties?.typeProperties?.computeProperties?.vNetProperties?.subnetId;
        if (subnetId === undefined || stringIsNullOrEmpty(subnetId)) {
            return null;
        }
        return subnetId;
    }

    public static GetVNetExpressSubnetId(properties: Model.ManagedIntegrationRuntime): string {
        if (properties != null && properties.typeProperties != null) {
            return properties.typeProperties.customerVirtualNetwork != null ? properties.typeProperties.customerVirtualNetwork.subnetId : '';
        }
        return null;
    }

    public static GetNumberOfNodes(properties: Model.ManagedIntegrationRuntime): number {
        if (properties != null &&
            properties.typeProperties != null &&
            properties.typeProperties.computeProperties != null
        ) {
            return !properties.typeProperties.computeProperties.numberOfNodes ? 0 : properties.typeProperties.computeProperties.numberOfNodes;
        }

        return 0;
    }

    public static GetPublicIps(properties: Model.ManagedIntegrationRuntime): string[] {
        if (properties != null &&
            properties.typeProperties != null &&
            properties.typeProperties.computeProperties != null &&
            properties.typeProperties.computeProperties.vNetProperties != null
        ) {
            return properties.typeProperties.computeProperties.vNetProperties.publicIPs == null ? [] : properties.typeProperties.computeProperties.vNetProperties.publicIPs;
        }
        return [];
    }

    public static GetIRHasNewVersion(versionStatus?: string): boolean {
        if (versionStatus === irVersionStatusConst.UpdateAvailable) {
            return true;
        }
        if (versionStatus === irVersionStatusConst.Expiring) {
            return true;
        }
        return false;
    }

    public static CalculateSSISIRStatus(irStatus: Model.ManagedIntegrationRuntimeStatus, deleteLockErrors?: Model.ManagedIntegrationRuntimeError[]): string {
        const status = irStatus.state;
        if (status == null) {
            return '';
        }
        if (this.IsIRRunning(status)) {
            const nodes = irStatus.typeProperties != null ? irStatus.typeProperties.nodes : undefined;
            if (nodes == null || nodes.length === 0) {
                return irStatusConst.Unavailable;
            }
            let hasNodeRunning = false;
            let hasNodeError = false;
            nodes.forEach((node) => {
                if (this.IsIRRunning(node.status)) {
                    hasNodeRunning = true;
                }
                if (node.errors != null && node.errors.length > 0) {
                    hasNodeError = true;
                }
            });
            if (!hasNodeRunning) {
                return irStatusConst.Unavailable;
            }

            if (hasNodeError) {
                return irStatusConst.Limited;
            }

            if ((irStatus.typeProperties.otherErrors != null && irStatus.typeProperties.otherErrors.length > 0)
                || (deleteLockErrors && deleteLockErrors.length > 0)) {
                return irStatusConst.Limited;
            }
        }
        return status;
    }

    public static CalculateSelfhostedIRStatus(irStatus: Model.SelfHostedIntegrationRuntimeStatus): string {
        const status = irStatus.state;
        if (status == null) {
            return '';
        }
        if (this.IsIRRunning(status)) {
            let groupLimited = false;
            const nodes = irStatus.typeProperties != null ? irStatus.typeProperties.nodes : undefined;
            if (nodes == null || nodes.length === 0) {
                return irStatusConst.Unavailable;
            }
            if (!this._shirEnabledSelfContained(irStatus) && this._shirInConnectionErrorWithSrvBus(irStatus)) {
                groupLimited = true;
            }
            nodes.forEach((node) => {
                const nodeStatus = this.CalculateIRNodeStatus(node, irStatus);
                if (nodeStatus !== irStatusConst.Online && nodeStatus !== irStatusConst.Available) {
                    groupLimited = true;
                }
            });
            if (groupLimited) {
                return irStatusConst.Limited;
            }
        }
        return status;
    }

    public static CalculateManagedVNetTooltip(irStatus: Model.ManagedIntegrationRuntimeStatus): string {
        const status = irStatus.state;
        if (status !== irStatusConst.Limited || irStatus.typeProperties == null) {
            return '';
        }
        const copyState = IntegrationRuntimeAdapters.LocalizeIRStatusString(irStatus.typeProperties.dataMovementState);
        const dataflowState = IntegrationRuntimeAdapters.LocalizeIRStatusString(irStatus.typeProperties.dataflowState);
        return format(stringResources.ManagedVNetSubstatusTooltip, copyState, dataflowState);
    }

    public static CalculateSelfhostedVNetTooltip(irStatus: Model.SelfHostedIntegrationRuntimeStatus): string {
        const status = irStatus.state;
        if ((status === irStatusConst.Offline || status === irStatusConst.Unavailable || status === irStatusConst.NeedRegistration) && irStatus.typeProperties != null) {
            return `<a target="_blank" href="https://go.microsoft.com/fwlink/?linkid=2143854">${stringResources.TroubleShooting}</a>`;
        }
        if (status === irStatusConst.Limited
            && irStatus.type === irTypesConst.SelfHosted
            && !this._shirEnabledSelfContained(irStatus)
            && this._shirInConnectionErrorWithSrvBus(irStatus)) {
            return stringResources.IRError_Selfhosted_ServiceBusConnection_GuideUserOptions;
        }
        return '';
    }

    public static CalculateIRNodeStatus(nodeStatus: Model.SelfHostedIntegrationRuntimeNode, irStatus: Model.SelfHostedIntegrationRuntimeStatus): string {
        const status = nodeStatus.status;
        if (status == null) {
            return '';
        }
        if (this.IsIRRunning(status)) {
            if (nodeStatus.concurrentJobsLimit === 0) {
                return irStatusConst.Inactive;
            }
            if (!stringsAreEqualCaseInsensitive(nodeStatus.version, irStatus.typeProperties.version)) {
                return irStatusConst.Inactive;
            }
            if (nodeStatus.capabilities != null) {
                const capabilities = nodeStatus.capabilities as IIRCapabilities;
                if (!this.getCapabilityBooleanValue(true, capabilities.connectedToResourceManager)) {
                    return irStatusConst.Inactive;
                }
                if (!this.getCapabilityBooleanValue(true, capabilities.nodeEnabled)) {
                    return irStatusConst.Inactive;
                }
                if (!this.getCapabilityBooleanValue(true, capabilities.credentialInSync)) {
                    return irStatusConst.Limited;
                }
                if (!this.getCapabilityBooleanValue(true, capabilities.httpsPortEnabled)) {
                    return irStatusConst.Limited;
                }
            }
        }
        return status;
    }
    //#endregion

    public static CalculateSSISIRErrorCount(ssisIRStatus: Model.ManagedIntegrationRuntimeStatus, deleteLockErrors?: Model.ManagedIntegrationRuntimeError[]): number {
        let nodeErrors: Model.ManagedIntegrationRuntimeError[] = [];
        let otherErrors: Model.ManagedIntegrationRuntimeError[] = [];
        if (ssisIRStatus.typeProperties.otherErrors != null) {
            otherErrors = otherErrors.concat(ssisIRStatus.typeProperties.otherErrors);
        }
        if (ssisIRStatus.typeProperties.nodes != null) {
            const nodeList = ssisIRStatus.typeProperties.nodes;
            if (nodeList.length > 0) {
                nodeErrors = nodeErrors.concat(nodeList.map((item) => item.errors != null ? item.errors : []).reduce((value1, value2) => value1.concat(value2)));
            }
        }

        const lastOperationFailed = ssisIRStatus.typeProperties.lastOperation != null &&
            stringsAreEqualCaseInsensitive(ssisIRStatus.typeProperties.lastOperation.result, 'Failed');
        return otherErrors.length + nodeErrors.length + (lastOperationFailed ? 1 : 0) + (deleteLockErrors != null ? deleteLockErrors.length : 0);
    }

    public static IsIRRunning(irStatus?: string): boolean {
        return (irStatus === irStatusConst.Online ||
            irStatus === irStatusConst.Limited ||
            irStatus === irStatusConst.Available ||
            irStatus === irStatusConst.Started);
    }

    public static IsIRConnected(irStatus?: string): boolean {
        return this.IsIRRunning(irStatus) || irStatus === irStatusConst.Inactive;
    }

    public static IsSSISIRStuckInStarting(ssisIRStatus: Model.ManagedIntegrationRuntimeStatus): boolean {
        // If there is no lastopertion show retry/cancel start button (stuck in RP)
        if (!ssisIRStatus.typeProperties || !ssisIRStatus.typeProperties.lastOperation) {
            return true;
        }

        // Stuck in starting more than irStartingStuckMs
        if (ssisIRStatus.typeProperties.lastOperation.startTime) {
            const startTime = new Date(ssisIRStatus.typeProperties.lastOperation.startTime);
            if (new Date().getTime() - startTime.getTime() > irStartingStuckMs) {
                return true;
            }
        }

        // IntegrationRuntimeStartingLongerThanUsual error
        if (ssisIRStatus.typeProperties.otherErrors !== undefined && ssisIRStatus.typeProperties.otherErrors != null) {
            const otherErrors = ssisIRStatus.typeProperties.otherErrors;
            if (otherErrors && otherErrors.findIndex((item) =>
                stringsAreEqualCaseInsensitive(item.code, 'IntegrationRuntimeStartingLongerThanUsual') ||
                stringsAreEqualCaseInsensitive(item.code, 'VNetResourceGroupLockedOrReferenceProvisionMessage')) > -1) {
                return true;
            }
        }

        return false;
    }

    public static IsSSISIRStuckInStopping(ssisIRStatus: Model.ManagedIntegrationRuntimeStatus): boolean {
        // If there is no lastopertion show retry stop button (stuck in RP)
        if (!ssisIRStatus.typeProperties || !ssisIRStatus.typeProperties.lastOperation) {
            return true;
        }

        // Stuck in stopping more than irStoppingStuckMs
        if (ssisIRStatus.typeProperties.lastOperation.startTime) {
            const startTime = new Date(ssisIRStatus.typeProperties.lastOperation.startTime);
            if (new Date().getTime() - startTime.getTime() > irStoppingStuckMs) {
                return true;
            }
        }

        // IntegrationRuntimeStoppingLongerThanUsual error
        if (ssisIRStatus.typeProperties.otherErrors != null) {
            const otherErrors = ssisIRStatus.typeProperties.otherErrors;
            if (otherErrors && otherErrors.findIndex((item) =>
                stringsAreEqualCaseInsensitive(item.code, 'IntegrationRuntimeStoppingLongerThanUsual') ||
                stringsAreEqualCaseInsensitive(item.code, 'VNetResourceGroupLockedOrReferenceDeprovisionMessage')) > -1) {
                return true;
            }
        }

        return false;
    }

    public static ConvertStatusIconClass(status?: string, errorCount?: number, toBeExpired: boolean = false): string {
        switch (status) {
            case irStatusConst.Online:
                return toBeExpired ? 'svg-i-16x16-StatusWarning' : 'svg-i-16x16-StatusSuccessful';
            case irStatusConst.Available:
                return 'svg-i-16x16-StatusSuccessful';

            case irStatusConst.Limited:
            case irStatusConst.Recycling:
            case irStatusConst.Inactive:
                return 'svg-i-16x16-StatusWarning';

            case irStatusConst.NeedRegistration:
            case irStatusConst.Offline:
            case irStatusConst.InitializeFailed:
            case irStatusConst.NotExist:
            case irStatusConst.Unauthorized:
            case irStatusConst.Unavailable:
            case irStatusConst.NotFound:
                return 'svg-i-16x16-StatusFailed';

            case irStatusConst.Upgrading:
                return 'svg-i-16x16-StatusInvalid';

            case irStatusConst.Stopped:
                if (errorCount != null && errorCount > 0) {
                    return 'svg-i-16x16-StatusFailed';
                }
                return 'svg-i_stopped_16x16';
            case irStatusConst.Started:
            case irStatusConst.Initial:
                if (errorCount != null && errorCount > 0) {
                    return 'svg-i-16x16-StatusFailed';
                }
                return 'svg-i-16x16-StatusSuccessful';

            case irStatusConst.Stopping:
            case irStatusConst.Starting:
            case irStatusConst.Initializing:
                if (errorCount != null && errorCount > 0) {
                    return 'svg-i-16x16-StatusFailed';
                }
                return 'svg-i-16x16-InProgress';
            default:
                return '';
        }
    }

    public static ConvertIRTypeIcon(irtype: string): string {
        switch (irtype) {
            case irTypesConst.AzureSSIS:
                return 'svg-i-64x64-IR-SSIS';
            case irTypesConst.AzureSSISShared:
                return 'svg-i-64x64-IR-SSIS';
            case irTypesConst.AzureSSISLinked:
                return 'svg-i-64x64-IR-LinkedSSIS';
            case irTypesConst.Azure:
                return 'svg-i-64x64-IR-AzurePublic';
            case irTypesConst.Linked:
                return 'svg-i-64x64-IR-LinkedSelfHosted';
            case irTypesConst.SelfHosted:
            case irTypesConst.Emulator:
                return 'svg-i-64x64-IR-SelfHosted';
            case irTypesConst.ManagedVNet:
                return 'svg-i-64x64-IR-AzureVnet';
            case irTypesConst.Airflow:
                return 'svg-airflow';
            default:
                return '';
        }
    }

    public static isLinkedIR(irType: string): boolean {
        return irType === irTypesConst.Linked;
    }

    public static isOnpremisesIR(irType: string): boolean {
        return irType === irTypesConst.SelfHosted || IntegrationRuntimeAdapters.isLinkedIR(irType);
    }

    public static isAzureDataMovementIR(irType: string): boolean {
        return irType === irTypesConst.Azure || irType === irTypesConst.ManagedVNet;
    }

    public static GetIRNameFromActivityRun(run: Model.ActivityRun, logger?: LoggerService): string {
        let integrationRuntimeName: string = '';
        const executionDetails: IActivityRunExecutionDetails = run.executionDetails;
        try {
            if (GlobalVariables.activityIRNodeMonitoring && executionDetails != null && executionDetails.integrationRuntime != null) {
                integrationRuntimeName = executionDetails.integrationRuntime.map((ir) => {
                    switch (ir.type) {
                        case irTypesConst.SelfHosted:
                            return ir.name + (ir.nodes == null ? '' : ` (${ir.nodes.map((node) => node.name).join(', ')})`);
                        case irTypesConst.Managed:
                            return ir.name + (ir.location == null ? '' : ` (${localizeRegionString(ir.location)})`);
                        default:
                            if (logger != null) {
                                logger.error(this.className, `GetIRNameFromActivityRun - Unexpected IR type, type name: ${ir.type}`);
                            }
                            return '';
                    }
                }).join('; ');
            }
        } catch (error) {
            if (logger != null) {
                logger.error(this.className, `GetIRNameFromActivityRun - Failed to parse IR information from executionDetails, error: ${convertObjectToString(error)}`);
            }
        }

        if (integrationRuntimeName === '' && run.output != null && !isControlActivityType(run.activityType)) {
            const output = run.output as IActivityRunOutput;
            integrationRuntimeName = getStringOrDefaultValue(stringResources.Unknown, output.effectiveIntegrationRuntime);
        }
        return integrationRuntimeName;
    }

    public static IsIRSupportActivityLog(run: Model.ActivityRun, logger?: LoggerService): boolean {
        const executionDetails: IActivityRunExecutionDetails = run.executionDetails;
        try {
            if (executionDetails != null && executionDetails.integrationRuntime != null && run.output != null) {
                const containSelfHostedIR = executionDetails.integrationRuntime.some((ir) => ir.type === irTypesConst.SelfHosted);
                const output = run.output as IActivityRunOutput;
                const integrationRuntimeName = getStringOrDefaultValue(stringResources.Unknown, output.effectiveIntegrationRuntime);
                // TODO staging activity will be supported after execution details refactor.
                const isStagingActivity = integrationRuntimeName.indexOf(';') >= 0;
                return containSelfHostedIR && !isStagingActivity;
            }
        } catch (error) {
            if (logger != null) {
                logger.error(this.className, `IsIRSupportActivityLog - Failed to parse IR information from executionDetails, error: ${convertObjectToString(error)}`);
            }
        }
        return false;
    }

    public static compareIRVersion(version1?: string, version2?: string): number {
        if (version1 == null || !version1) {
            return -1;
        }
        if (version2 == null || !version2) {
            return 1;
        }
        const [majorVersion1, minorVersion1] = version1.split('.').map((value) => parseInt(value, 10));
        const [majorVersion2, minorVersion2] = version2.split('.').map((value) => parseInt(value, 10));
        if (majorVersion1 > majorVersion2) {
            return 1;
        } else if (majorVersion1 < majorVersion2) {
            return -1;
        } else {
            if (minorVersion1 > minorVersion2) {
                return 1;
            } else if (minorVersion1 < minorVersion2) {
                return -1;
            } else {
                return 0;
            }
        }
    }

    public static GetPackageStoreList(properties: Model.ManagedIntegrationRuntime): Model.PackageStore[] {
        if (properties && properties.typeProperties && properties.typeProperties.ssisProperties && properties.typeProperties.ssisProperties.packageStores) {
            return properties.typeProperties.ssisProperties.packageStores;
        }
        return [];
    }

    public static getIRIconStatus(response: IGetStatusResult<Model.IntegrationRuntimeStatus>, irType: string): string {
        const errorStatusResponse = IntegrationRuntimeAdapters.getResponseNotSucceededResponse<Model.IntegrationRuntimeStatus, string>(
            response,
            'svg-i-16x16-StatusFailed',
            '',
            undefined,
            undefined,
            'svg-i-16x16-InProgress',
            undefined
        );

        if (errorStatusResponse != null) {
            return errorStatusResponse;
        }

        let status = response.result.state;
        let errorCount = 0;

        if (irType === irTypesConst.AzureSSIS) {
            const azureSSISIRStatus = response.result as Model.ManagedIntegrationRuntimeStatus;
            status = IntegrationRuntimeAdapters.CalculateSSISIRStatus(azureSSISIRStatus);
            errorCount = IntegrationRuntimeAdapters.CalculateSSISIRErrorCount(azureSSISIRStatus);
        }
        if (IntegrationRuntimeAdapters.isOnpremisesIR(irType)) {
            status = IntegrationRuntimeAdapters.CalculateSelfhostedIRStatus(response.result as Model.SelfHostedIntegrationRuntimeStatus);
        }

        return IntegrationRuntimeAdapters.ConvertStatusIconClass(status, errorCount);
    }

    public static getIRLocalizedStatus(response: IGetStatusResult<Model.IntegrationRuntimeStatus>, irType: string): string {
        const errorStatusResponse = IntegrationRuntimeAdapters.getResponseNotSucceededResponse<Model.IntegrationRuntimeStatus, string>(
            response,
            stringResources.IRError_FailedToGetStatus,
            stringResources.Refreshing,
            IntegrationRuntimeAdapters.LocalizeIRStatusString(irStatusConst.Unauthorized),
            IntegrationRuntimeAdapters.LocalizeIRStatusString(irStatusConst.Unavailable),
            stringResources.UpdatingWithoutEclipse,
            IntegrationRuntimeAdapters.LocalizeIRStatusString(irStatusConst.NotFound)
        );

        if (!stringIsNullOrEmpty(errorStatusResponse)) {
            return errorStatusResponse;
        }

        let status = response.result.state;
        let errorCount = 0;

        if (irType === irTypesConst.AzureSSIS) {
            const azureSSISIRStatus = response.result as Model.ManagedIntegrationRuntimeStatus;
            status = IntegrationRuntimeAdapters.CalculateSSISIRStatus(azureSSISIRStatus);
            errorCount = IntegrationRuntimeAdapters.CalculateSSISIRErrorCount(azureSSISIRStatus);
            const lastOperationType = azureSSISIRStatus && azureSSISIRStatus.typeProperties && azureSSISIRStatus.typeProperties.lastOperation
                ? azureSSISIRStatus.typeProperties.lastOperation.type : '';
            return IntegrationRuntimeAdapters.LocalizeIRStatusString(status, errorCount, lastOperationType);
        }
        if (IntegrationRuntimeAdapters.isOnpremisesIR(irType)) {
            status = IntegrationRuntimeAdapters.CalculateSelfhostedIRStatus(response.result as Model.SelfHostedIntegrationRuntimeStatus);
        }
        return IntegrationRuntimeAdapters.LocalizeIRStatusString(status, errorCount);
    }

    public static getResponseNotSucceededResponse<T, S>(response: IGetStatusResult<T>, notSucceeded: S, getting: S, unauthorized?: S, conflict?: S, updating?: S, notFound?: S): S | undefined {
        if (response.status === 'Getting') {
            return getting;
        }

        if (response.status === 'Updating') {
            return updating;
        }

        if (response.status === 'Unauthorized') {
            return unauthorized == null ? notSucceeded : unauthorized;
        }

        if (response.status === 'Conflict') {
            return conflict == null ? notSucceeded : conflict;
        }

        if (response.status === 'NotFound') {
            return notFound == null ? notSucceeded : notFound;
        }

        if (response.status !== 'Succeeded' || response.result == null) {
            return notSucceeded;
        }

        return undefined;
    }

    public static getCapabilityBooleanValue(defaultValue: boolean, str?: string): boolean {
        if (str == null) {
            return defaultValue;
        }
        if (stringsAreEqualCaseInsensitive(str, 'true')) {
            return true;
        }
        if (stringsAreEqualCaseInsensitive(str, 'false')) {
            return false;
        }
        return defaultValue;
    }

    private static LocalizeIRStatusTooltip(status?: string, errorCount?: number, originStatus?: string): string {
        switch (status) {
            case irStatusConst.NeedRegistration:
                return stringResources.IRStatus_Tooltip_NeedRegistration;
            case irStatusConst.Limited:
                if (errorCount != null && errorCount > 0) {
                    return stringResources.IRStatus_Tooltip_Error;
                }
                if (originStatus != null) {
                    if (originStatus === irStatusConst.Online) {
                        return stringResources.IRStatus_Tooltip_LimitedWorker;
                    }
                    return stringResources.IRStatus_Tooltip_LimitedWorker;
                }
                return stringResources.IRStatus_Tooltip_Limited;
            case irStatusConst.Offline:
                return stringResources.IRStatus_Tooltip_Offline;
            case irStatusConst.Initial:
            case irStatusConst.Stopped:
            case irStatusConst.Stopping:
            case irStatusConst.Started:
            case irStatusConst.Starting:
            case irStatusConst.Unavailable:
                if (errorCount != null && errorCount > 0) {
                    return stringResources.IRStatus_Tooltip_Error;
                }
                return '';
            default:
                return '';
        }
    }

    private static _shirEnabledSelfContained(irStatus: Model.SelfHostedIntegrationRuntimeStatus): boolean {
        if (irStatus.type === irTypesConst.SelfHosted
            && irStatus.typeProperties.selfContainedInteractiveAuthoringEnabled) {
            return true;
        }
        return false;
    }

    private static _shirInConnectionErrorWithSrvBus(irStatus: Model.SelfHostedIntegrationRuntimeStatus): boolean {
        return irStatus.typeProperties.nodes.some((node) => !this.getCapabilityBooleanValue(true, node.capabilities.serviceBusConnected));
    }
}
